import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { useVersionCheck } from '../hooks/useVersionCheck';

/**
 * Stale-frontend version controller (app/dashboard SPA only).
 *
 * When `useVersionCheck` detects the running bundle is older than the deployed
 * build, this arms an auto-refresh that fires at a SAFE MOMENT — never
 * mid-task, so no in-progress work is lost:
 *   1. the next react-router navigation → a full `window.location.assign` to
 *      the new route (lands the user on the fresh build), OR
 *   2. the tab regaining focus while NO editable field is dirty/focused →
 *      `window.location.reload()`.
 * Meanwhile a subtle, non-blocking "Updating…" indicator is shown.
 *
 * Loop safety: this focus/route path only ever *arms*; each auto-reload is
 * guarded once-per-target-build via sessionStorage, so a lagging version.json
 * (CDN edge still serving old assets) can't produce a reload storm. The eager
 * `vite:preloadError` recovery lives in main.tsx (separate guard key).
 *
 * NEVER runs inside the embedded widget iframe route (`/widget/:agentId`,
 * PROTECTED #1) — a hard reload there would drop a live chat/voice/avatar
 * session. The checker is disabled and nothing renders on that route.
 */

const guardKey = (id: string) => `reloadedForBuild:${id}`;

function alreadyReloadedFor(id: string): boolean {
  try {
    return sessionStorage.getItem(guardKey(id)) === '1';
  } catch {
    return false;
  }
}

function markReloadedFor(id: string): void {
  try {
    sessionStorage.setItem(guardKey(id), '1');
  } catch {
    /* sessionStorage unavailable — best-effort only */
  }
}

// True when focus is inside, or unsaved text lives within, any editable field
// — the signal that reloading now would lose the user's work. Text-bearing
// inputs/textareas and contenteditable regions count; buttons, checkboxes,
// files, etc. do not.
function hasUnsavedInput(): boolean {
  const active = document.activeElement as HTMLElement | null;
  if (active) {
    const tag = active.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || active.isContentEditable) {
      return true;
    }
  }

  const fields = document.querySelectorAll<HTMLElement>(
    'input, textarea, [contenteditable="true"], [contenteditable=""]',
  );
  for (const field of Array.from(fields)) {
    if (field instanceof HTMLInputElement) {
      const type = field.type;
      if (
        type === 'button' ||
        type === 'submit' ||
        type === 'reset' ||
        type === 'checkbox' ||
        type === 'radio' ||
        type === 'hidden' ||
        type === 'file' ||
        type === 'range'
      ) {
        continue;
      }
      if (field.value.trim() !== '') return true;
    } else if (field instanceof HTMLTextAreaElement) {
      if (field.value.trim() !== '') return true;
    } else if (field.isContentEditable) {
      if ((field.textContent || '').trim() !== '') return true;
    }
  }
  return false;
}

/**
 * A polite status message plus ONE control — the manual "refresh now" escape
 * hatch. Everything except that button is inert.
 *
 * The live region is the inner status row ONLY. The button deliberately sits
 * OUTSIDE it: a control announced as part of a polite live region is read out
 * as changing status text rather than offered as something to press, and it
 * would be re-announced on every update to the message beside it.
 *
 * Placement rules, all of them load-bearing:
 *   - `pointer-events-none` stays on the CONTAINER; only the button re-enables
 *     it (`pointer-events-auto`). A status chip must never intercept a tap —
 *     it previously sat over the sidebar's Logout button and swallowed it, so
 *     the padding, the rounded-full area and the text all remain click-through
 *     and only the button's own box is hittable.
 *   - `z-40`, NOT above the sidebar. The sidebar is `z-50` and every blocker
 *     (trial / subscription / maintenance) sits at `z-40` precisely so the
 *     logout escape hatch stays reachable — see Layout.tsx:83-85. The other
 *     `z-[60]` users in this app are all `inset-0` modals, which are allowed
 *     above the sidebar because they're dismissible and take focus. A
 *     persistent chip is not, so it belongs under the sidebar.
 *   - Right column, not left: on desktop the sidebar is always visible
 *     (`w-64` / `lg:w-20`), so a `left-4` chip at `z-40` would be painted
 *     underneath it and never seen.
 *   - `bottom-24` (96px): clears the injected ProdagiHelpWidget launcher
 *     bubble (60px tall at bottom:20px → top edge 80px) and the toast stack
 *     (`bottom-4`, `z-[100]`). SetupTour's dock picked the same floor for the
 *     same reason. When that dock is open it paints over this chip (`z-50`);
 *     the refresh button is then unreachable until the dock is closed, which
 *     is acceptable — the automatic reload path is untouched and still fires.
 *   - safe-area inset so iOS's home indicator doesn't clip it.
 */
const UpdatingIndicator: React.FC<{ onRefresh: () => void }> = ({
  onRefresh,
}) => (
  <div className="pointer-events-none select-none fixed right-4 bottom-[calc(6rem_+_env(safe-area-inset-bottom))] z-40 flex items-center gap-2 max-w-[calc(100vw-2rem)] rounded-full border border-gray-200 dark:border-gray-700 bg-white/90 dark:bg-gray-900/90 backdrop-blur pl-3.5 pr-1.5 py-1.5 shadow-lg">
    <div
      role="status"
      aria-live="polite"
      className="flex min-w-0 items-center gap-2"
    >
      <RefreshCw
        className="w-4 h-4 shrink-0 text-primary animate-spin"
        aria-hidden="true"
      />
      <span className="text-xs font-medium text-gray-700 dark:text-gray-200 truncate">
        Updating to the latest version…
      </span>
    </div>
    <button
      type="button"
      onClick={onRefresh}
      className="pointer-events-auto shrink-0 rounded-full bg-primary/10 dark:bg-primary/25 px-2.5 py-1 text-xs font-semibold text-primary-700 dark:text-primary-200 whitespace-nowrap transition-colors hover:bg-primary/20 dark:hover:bg-primary/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-white dark:focus-visible:ring-offset-gray-900"
    >
      Refresh now
    </button>
  </div>
);

const VersionUpdateManager: React.FC = () => {
  const location = useLocation();

  // Hard exclude the embedded widget iframe route (PROTECTED #1).
  const isWidget =
    location.pathname === '/widget' || location.pathname.startsWith('/widget/');

  const { outdated, latestBuildId } = useVersionCheck(!isWidget);

  // Mirror the latest values into refs so the once-registered event handlers
  // (focus, navigation) always read current state without re-subscribing.
  const outdatedRef = useRef(false);
  const latestRef = useRef<string | null>(null);
  useEffect(() => {
    outdatedRef.current = outdated;
    latestRef.current = latestBuildId;
  }, [outdated, latestBuildId]);

  const doReload = (target?: string) => {
    const id = latestRef.current;
    if (!id) return;
    if (alreadyReloadedFor(id)) return; // one auto-reload per target build per session
    markReloadedFor(id);
    if (target) {
      window.location.assign(target);
    } else {
      window.location.reload();
    }
  };

  // Manual escape hatch — the chip's "Refresh now" button. An explicit click
  // must ALWAYS reload, so it deliberately does NOT go through `doReload`,
  // which returns early both when `latestRef` is null and when the
  // once-per-build sessionStorage guard is already set. Either would leave a
  // deliberate press silently doing nothing. It likewise does not STAMP that
  // guard: the guard exists to cap the automatic path at one reload per build,
  // and a user pressing a button they can see is not that path. Leaving it
  // untouched keeps the automatic behaviour exactly as it was.
  const reloadNow = () => {
    window.location.reload();
  };

  // Safe moment #1 — next react-router navigation. `location.key` changes on
  // every navigation; the initial mount run is a no-op (not yet outdated).
  // We hard-load the NEW route on the fresh build, replacing the client-side
  // SPA transition so the user lands on updated code with zero work lost.
  useEffect(() => {
    if (isWidget) return;
    if (outdatedRef.current) {
      doReload(location.pathname + location.search + location.hash);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Safe moment #2 — tab regains focus with no unsaved/in-progress input.
  // If a field is dirty or focused, we defer to the next navigation instead.
  useEffect(() => {
    if (isWidget) return;
    const onFocus = () => {
      if (!outdatedRef.current) return;
      if (hasUnsavedInput()) return;
      doReload();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWidget]);

  if (isWidget || !outdated) return null;

  // `doReload` fires at most once per target build per session (sessionStorage
  // guard), and `useVersionCheck` latches `outdated` one-way and stops polling.
  // So once the guard is set, no further reload will EVER happen this session —
  // if version.json still mismatches (lagging CDN edge, failed deploy), the
  // chip would otherwise sit there claiming "Updating to the latest version…"
  // forever, non-dismissible and untrue. Hide it once we've done all we're
  // going to do; a later navigation still lands the user on fresh code.
  if (latestBuildId && alreadyReloadedFor(latestBuildId)) return null;

  return <UpdatingIndicator onRefresh={reloadNow} />;
};

export default VersionUpdateManager;
